import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectStateroomConfig } from "../../../store/selectors/shipBuilderSelectors";
import {
  setStateroomConfig,
  setPassengerCount,
  setPassengerCargoOverride,
} from "../../../store/features/shipBuilderSlice";
import { setSectionStateroomConfig } from "../../../store/features/breakawayHullSlice";
import { formatCurrency, formatNumber } from "../../../utils/formatting";
import { STATEROOM_TYPES } from "../../../constants/stateroom";
import {
  LOW_BERTH_SPEC,
  EMERGENCY_LOW_BERTH_SPEC,
  COMMON_AREA_SPEC,
} from "../../../constants/stateroom";
import { selectors as shipBuilderSelectors } from "../../../store/selectors/shipBuilderSelectors";
import { selectors } from "../../../store/selectors";
import { selectIsBreakaway } from "../../../store/selectors/breakawayHullSelectors";

interface SectionStateroomAssignment {
  sectionId: string;
  sectionName: string;
  sectionSize: number;
  singleStaterooms: number;
  doubleStaterooms: number;
  lowBerths: number;
  emergencyLowBerths: number;
  commonArea: number;
}

const BreakawayStateroomStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const stateroomConfig = useAppSelector(selectStateroomConfig);
  const stewardsRequired = useAppSelector(
    shipBuilderSelectors.selectStewardsRequired,
  );

  // Breakaway hulls detection
  const isBreakaway = useAppSelector(selectIsBreakaway);
  const breakawayHull = useAppSelector((state) => state.breakawayHull);
  const hasBreakawayHulls = isBreakaway || breakawayHull?.isBreakaway;

  const [sectionAssignments, setSectionAssignments] = useState<SectionStateroomAssignment[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Use selectors for all summary/derived values
  const assignedCrewCount = useAppSelector(
    selectors.derived.selectAssignedCrewCount,
  );
  const stateroomNeeds = (useAppSelector(
    selectors.derived.selectStateroomNeeds,
  ) || {}) as any;

  const totalPassengerCargoTons = useAppSelector(
    selectors.derived.selectTotalPassengerCargoTons,
  ) as number;
  const totalStaterooms = useAppSelector(
    selectors.derived.selectTotalStaterooms,
  ) as number;
  const totalCapacity = useAppSelector(
    selectors.derived.selectTotalStateroomCapacity,
  ) as number;
  const optionalCommonAreaTonnage = useAppSelector(
    selectors.derived.selectOptionalSystemsCommonAreaTonnage,
  ) as number;
  const recommendedCommonArea = useAppSelector(
    selectors.derived.selectRecommendedCommonArea,
  ) as number;

  const showCommonAreaWarning = useAppSelector(
    selectors.derived.selectShowCommonAreaWarning,
  );

  // New selector for comprehensive hierarchical stateroom allocation
  const hierarchicalAllocation = useAppSelector(
    selectors.derived.selectHierarchicalStateroomAllocation,
  );

  // Selectors for accommodation breakdown
  const highStateroomQuantity = useAppSelector((state) => {
    const accommodations = state.optionalSystems.accommodations || [];
    return accommodations
      .filter(
        (s) =>
          s &&
          typeof s === "object" &&
          s.type === "accommodation" &&
          s.subtype === "highStateroom",
      )
      .reduce(
        (sum: number, s) =>
          sum +
          (typeof s.properties?.quantity === "number"
            ? s.properties.quantity
            : 1),
        0,
      );
  });

  const luxuryStateroomQuantity = useAppSelector((state) => {
    const accommodations = state.optionalSystems.accommodations || [];
    return accommodations
      .filter(
        (s) =>
          s &&
          typeof s === "object" &&
          s.type === "accommodation" &&
          s.subtype === "luxuryStateroom",
      )
      .reduce(
        (sum: number, s) =>
          sum +
          (typeof s.properties?.quantity === "number"
            ? s.properties.quantity
            : 1),
        0,
      );
  });

  const barracksCapacity = useAppSelector((state) => {
    const accommodations = state.optionalSystems.accommodations || [];
    return accommodations
      .filter(
        (s) =>
          s &&
          typeof s === "object" &&
          s.type === "accommodation" &&
          s.subtype === "barracks",
      )
      .reduce(
        (sum: number, s) =>
          sum +
          (typeof s.properties?.quantity === "number"
            ? s.properties.quantity
            : 1),
        0,
      );
  });

  // Additional selectors for crew requirements
  const barracksCrewCapacity = useAppSelector(selectors.derived.selectBarracksCrewCapacity);

  // --- Handler functions ---
  const handlePassengerCountChange = (
    type: "high" | "middle" | "basic" | "low",
    value: number,
  ) => {
    dispatch(setPassengerCount({ type, count: value }));
  };

  const handleCargoOverrideChange = (
    type: "high" | "middle" | "basic" | "low",
    value: number,
  ) => {
    dispatch(setPassengerCargoOverride({ type, value }));
  };

  const resetCargoToDefaults = () => {
    dispatch(setPassengerCargoOverride({ type: "high", value: 1 }));
    dispatch(setPassengerCargoOverride({ type: "middle", value: 100 }));
    dispatch(setPassengerCargoOverride({ type: "basic", value: 10 }));
    dispatch(setPassengerCargoOverride({ type: "low", value: 10 }));
  };

  // Initialize assignments from existing stateroom configuration
  useEffect(() => {
    if (breakawayHull?.sections) {
      const newAssignments: SectionStateroomAssignment[] = [];

      // Calculate total stateroom requirements for the whole ship
      const totalSingleStaterooms = stateroomConfig?.single || 0;
      const totalDoubleStaterooms = stateroomConfig?.double || 0;
      const totalLowBerths = stateroomConfig?.lowBerths?.count || 0;
      const totalEmergencyLowBerths = stateroomConfig?.emergencyLowBerths?.count || 0;
      const totalCommonArea = stateroomConfig?.commonArea || 0;

      // Distribute staterooms proportionally across sections based on section size
      const totalShipSize = breakawayHull.sections.reduce((total, section) => total + section.size, 0);

      breakawayHull.sections.forEach(section => {
        const sectionRatio = section.size / totalShipSize;
        
        newAssignments.push({
          sectionId: section.id,
          sectionName: section.name,
          sectionSize: section.size,
          singleStaterooms: Math.floor(totalSingleStaterooms * sectionRatio),
          doubleStaterooms: Math.floor(totalDoubleStaterooms * sectionRatio),
          lowBerths: Math.floor(totalLowBerths * sectionRatio),
          emergencyLowBerths: Math.floor(totalEmergencyLowBerths * sectionRatio),
          commonArea: Math.floor(totalCommonArea * sectionRatio),
        });
      });

      setSectionAssignments(newAssignments);
      
      // Dispatch initial stateroom configurations to Redux using setTimeout to avoid React error
      setTimeout(() => {
        newAssignments.forEach(assignment => {
          dispatch(setSectionStateroomConfig({
            sectionId: assignment.sectionId,
            stateroomConfig: {
              singleStaterooms: assignment.singleStaterooms,
              doubleStaterooms: assignment.doubleStaterooms,
              lowBerths: assignment.lowBerths,
              emergencyLowBerths: assignment.emergencyLowBerths,
              commonArea: assignment.commonArea,
            }
          }));
        });
      }, 0);
    }
  }, [breakawayHull?.sections, stateroomConfig, dispatch]);

  const handleAssignmentChange = (sectionId: string, field: keyof Omit<SectionStateroomAssignment, 'sectionId' | 'sectionName' | 'sectionSize'>, value: number) => {
    setSectionAssignments(prev => {
      const updatedAssignments = prev.map(assignment => 
        assignment.sectionId === sectionId
          ? { ...assignment, [field]: Math.max(0, value) }
          : assignment
      );
      
      // Update Redux state with the new stateroom configuration using setTimeout to avoid React error
      setTimeout(() => {
        const updatedAssignment = updatedAssignments.find(a => a.sectionId === sectionId);
        if (updatedAssignment) {
          dispatch(setSectionStateroomConfig({
            sectionId,
            stateroomConfig: {
              singleStaterooms: updatedAssignment.singleStaterooms,
              doubleStaterooms: updatedAssignment.doubleStaterooms,
              lowBerths: updatedAssignment.lowBerths,
              emergencyLowBerths: updatedAssignment.emergencyLowBerths,
              commonArea: updatedAssignment.commonArea,
            }
          }));
        }
      }, 0);
      
      return updatedAssignments;
    });
    setErrors(prev => ({ ...prev, [`${sectionId}-${field}`]: '' }));
  };

  const getTotalAssigned = (field: keyof Omit<SectionStateroomAssignment, 'sectionId' | 'sectionName' | 'sectionSize'>) => {
    return sectionAssignments.reduce((sum, assignment) => sum + assignment[field], 0);
  };

  const getOriginalTotal = (field: keyof Omit<SectionStateroomAssignment, 'sectionId' | 'sectionName' | 'sectionSize'>) => {
    switch (field) {
      case 'singleStaterooms':
        return stateroomConfig?.single || 0;
      case 'doubleStaterooms':
        return stateroomConfig?.double || 0;
      case 'lowBerths':
        return stateroomConfig?.lowBerths?.count || 0;
      case 'emergencyLowBerths':
        return stateroomConfig?.emergencyLowBerths?.count || 0;
      case 'commonArea':
        return stateroomConfig?.commonArea || 0;
      default:
        return 0;
    }
  };

  const validateAssignments = () => {
    const newErrors: Record<string, string> = {};
    
    ['singleStaterooms', 'doubleStaterooms', 'lowBerths', 'emergencyLowBerths', 'commonArea'].forEach(field => {
      const totalAssigned = getTotalAssigned(field as keyof Omit<SectionStateroomAssignment, 'sectionId' | 'sectionName' | 'sectionSize'>);
      const originalTotal = getOriginalTotal(field as keyof Omit<SectionStateroomAssignment, 'sectionId' | 'sectionName' | 'sectionSize'>);
      
      if (totalAssigned !== originalTotal) {
        newErrors[field] = `Total assigned (${totalAssigned}) must equal original total (${originalTotal})`;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateAssignments()) {
      return;
    }

    // Update the main stateroom config with the totals
    const newConfig = {
      ...stateroomConfig,
      single: getTotalAssigned('singleStaterooms'),
      double: getTotalAssigned('doubleStaterooms'),
      lowBerths: {
        ...stateroomConfig?.lowBerths,
        count: getTotalAssigned('lowBerths'),
      },
      emergencyLowBerths: {
        ...stateroomConfig?.emergencyLowBerths,
        count: getTotalAssigned('emergencyLowBerths'),
      },
      commonArea: getTotalAssigned('commonArea'),
    };

    dispatch(setStateroomConfig(newConfig));
  };

  const calculateSectionTotals = (assignment: SectionStateroomAssignment) => {
    const singleCost = assignment.singleStaterooms * STATEROOM_TYPES.STANDARD.cost;
    const doubleCost = assignment.doubleStaterooms * STATEROOM_TYPES.STANDARD.cost;
    const lowBerthCost = assignment.lowBerths * LOW_BERTH_SPEC.cost;
    const emergencyLowBerthCost = assignment.emergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.cost;
    const commonAreaCost = assignment.commonArea * COMMON_AREA_SPEC.costPerTon;

    const singleTons = assignment.singleStaterooms * STATEROOM_TYPES.STANDARD.tons;
    const doubleTons = assignment.doubleStaterooms * STATEROOM_TYPES.STANDARD.tons;
    const lowBerthTons = assignment.lowBerths * LOW_BERTH_SPEC.tons;
    const emergencyLowBerthTons = assignment.emergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.tons;
    const commonAreaTons = assignment.commonArea;

    const singlePower = assignment.singleStaterooms * STATEROOM_TYPES.STANDARD.power;
    const doublePower = assignment.doubleStaterooms * STATEROOM_TYPES.STANDARD.power;
    const lowBerthPower = Math.ceil(assignment.lowBerths / 10) * LOW_BERTH_SPEC.power;
    const emergencyLowBerthPower = assignment.emergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.power;
    const commonAreaPower = assignment.commonArea * COMMON_AREA_SPEC.powerPerTon;

    return {
      cost: singleCost + doubleCost + lowBerthCost + emergencyLowBerthCost + commonAreaCost,
      tons: singleTons + doubleTons + lowBerthTons + emergencyLowBerthTons + commonAreaTons,
      power: singlePower + doublePower + lowBerthPower + emergencyLowBerthPower + commonAreaPower,
    };
  };

  const calculateShipTotals = () => {
    return sectionAssignments.reduce((totals, assignment) => {
      const sectionTotals = calculateSectionTotals(assignment);
      return {
        cost: totals.cost + sectionTotals.cost,
        tons: totals.tons + sectionTotals.tons,
        power: totals.power + sectionTotals.power,
      };
    }, { cost: 0, tons: 0, power: 0 });
  };

  if (!hasBreakawayHulls || !breakawayHull?.sections) {
    return (
      <div className="w-full pb-32">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
            Breakaway Staterooms
          </h2>
          <p className="text-gray-600 dark:text-gray-300">No breakaway hull configuration detected.</p>
        </div>
      </div>
    );
  }

  const shipTotals = calculateShipTotals();

  return (
    <div className="flex flex-col md:flex-row gap-8 pb-32">
      {/* Left Column: Inputs */}
      <div className="flex-1 space-y-6">
        {/* Breakaway Hulls Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
                Breakaway Hulls Configuration
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                {breakawayHull?.sections?.length || 0} section{(breakawayHull?.sections?.length || 0) > 1 ? 's' : ''} 
                ({breakawayHull?.sections?.map(s => `${s.name}: ${s.size} tons`).join(", ") || ''})
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <strong>Note:</strong> Allocate staterooms to specific sections. Each section can have all, some, or none of the available staterooms.
              </p>
            </div>
          </div>
        </div>

        {/* Top Row: Passenger & Crew Input and Living Spaces side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Passenger Counts */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 shadow">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <span className="text-blue-500">👥</span> Passenger Counts
            </h3>
            {/* Passenger Passage Types */}
            <div className="space-y-4">
              {/* High Passage */}
              <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      High Passage
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.highCount}
                      onChange={(e) =>
                        handlePassengerCountChange(
                          "high",
                          Number(e.target.value),
                        )
                      }
                      min={0}
                      step={1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100"
                    />
                  </div>
                  <div className="w-32">
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Cargo Allocation (tons)
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.highCargo}
                      onChange={(e) =>
                        handleCargoOverrideChange(
                          "high",
                          Number(e.target.value),
                        )
                      }
                      min={0}
                      step={0.1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded text-xs dark:bg-gray-600 dark:text-gray-100"
                    />
                  </div>
                </div>
              </div>
              {/* Middle Passage */}
              <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Middle Passage
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.middleCount}
                      onChange={(e) =>
                        handlePassengerCountChange(
                          "middle",
                          Number(e.target.value),
                        )
                      }
                      min={0}
                      step={1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100"
                    />
                  </div>
                  <div className="w-32">
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Cargo Override (kg)
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.middleCargo}
                      onChange={(e) =>
                        handleCargoOverrideChange(
                          "middle",
                          Number(e.target.value),
                        )
                      }
                      min={0}
                      step={1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded text-xs dark:bg-gray-600 dark:text-gray-100"
                    />
                  </div>
                </div>
              </div>
              {/* Basic Passage */}
              <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Basic Passage
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.basicCount}
                      onChange={(e) =>
                        handlePassengerCountChange(
                          "basic",
                          Number(e.target.value),
                        )
                      }
                      min={0}
                      step={1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100"
                    />
                  </div>
                  <div className="w-32">
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Cargo Override (kg)
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.basicCargo}
                      onChange={(e) =>
                        handleCargoOverrideChange(
                          "basic",
                          Number(e.target.value),
                        )
                      }
                      min={0}
                      step={1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded text-xs dark:bg-gray-600 dark:text-gray-100"
                    />
                  </div>
                </div>
              </div>
              {/* Low Passage */}
              <div className="flex flex-col gap-1">
                <div className="flex flex-row gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Low Passage
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.lowCount}
                      onChange={(e) =>
                        handlePassengerCountChange(
                          "low",
                          Number(e.target.value),
                        )
                      }
                      min={0}
                      step={1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-gray-100"
                    />
                  </div>
                  <div className="w-32">
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Cargo Override (kg)
                    </label>
                    <input
                      type="number"
                      value={stateroomNeeds.lowCargo}
                      onChange={(e) =>
                        handleCargoOverrideChange("low", Number(e.target.value))
                      }
                      min={0}
                      step={1}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded text-xs dark:bg-gray-600 dark:text-gray-100"
                    />
                  </div>
                </div>
              </div>
              <button
                onClick={resetCargoToDefaults}
                className="w-full mt-2 px-3 py-1 text-xs bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded text-gray-800 dark:text-gray-100"
              >
                Reset Cargo to Defaults
              </button>
            </div>
          </div>

          {/* Living Spaces */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 shadow">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100">
              <span className="text-green-500">🏠</span> Living Spaces
            </h3>
            <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Single Staterooms
                </label>
                <input
                  type="number"
                  value={stateroomConfig?.single || 0}
                  onChange={(e) => {
                    // This will be handled by section assignments
                  }}
                  min={0}
                  step={1}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-600"
                  disabled
                />
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Configured via section assignments below
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Double Staterooms
                </label>
                <input
                  type="number"
                  value={stateroomConfig?.double || 0}
                  onChange={(e) => {
                    // This will be handled by section assignments
                  }}
                  min={0}
                  step={1}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-600"
                  disabled
                />
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Configured via section assignments below
                </div>
              </div>
            </div>

            {/* Accommodation Breakdown */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    High Staterooms
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {highStateroomQuantity}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Luxury Staterooms
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {luxuryStateroomQuantity}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Barracks
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {barracksCapacity}
                  </div>
                </div>
              </div>
            </div>

            {/* Horizontal info bar */}
            <div className="flex flex-wrap gap-4 mt-6 text-sm justify-between">
              <div>
                <span className="block text-gray-500 dark:text-gray-400">Total Staterooms</span>
                <span className="font-bold text-gray-900 dark:text-gray-100">{totalStaterooms}</span>
              </div>
              <div>
                <span className="block text-gray-500 dark:text-gray-400">Total Capacity</span>
                <span className="font-bold text-gray-900 dark:text-gray-100">{totalCapacity} people</span>
              </div>
            </div>
          </div>
        </div>

        {/* Section Assignments */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Section Stateroom Assignments
          </h3>
          <div className="space-y-6">
            {sectionAssignments.map((assignment) => {
              const sectionTotals = calculateSectionTotals(assignment);
              
              return (
                <div key={assignment.sectionId} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100">
                      {assignment.sectionName} Section
                    </h4>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {assignment.sectionSize} tons
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {/* Single Staterooms */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Single Staterooms
                      </label>
                      <input
                        type="number"
                        value={assignment.singleStaterooms}
                        onChange={(e) => handleAssignmentChange(assignment.sectionId, 'singleStaterooms', Number(e.target.value))}
                        min={0}
                        step={1}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-gray-100"
                      />
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {assignment.singleStaterooms * STATEROOM_TYPES.STANDARD.tons} tons
                      </div>
                    </div>

                    {/* Double Staterooms */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Double Staterooms
                      </label>
                      <input
                        type="number"
                        value={assignment.doubleStaterooms}
                        onChange={(e) => handleAssignmentChange(assignment.sectionId, 'doubleStaterooms', Number(e.target.value))}
                        min={0}
                        step={1}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-gray-100"
                      />
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {assignment.doubleStaterooms * STATEROOM_TYPES.STANDARD.tons} tons
                      </div>
                    </div>

                    {/* Low Berths */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Low Berths
                      </label>
                      <input
                        type="number"
                        value={assignment.lowBerths}
                        onChange={(e) => handleAssignmentChange(assignment.sectionId, 'lowBerths', Number(e.target.value))}
                        min={0}
                        step={1}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-gray-100"
                      />
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {assignment.lowBerths * LOW_BERTH_SPEC.tons} tons
                      </div>
                    </div>

                    {/* Emergency Low Berths */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Emergency Low Berths
                      </label>
                      <input
                        type="number"
                        value={assignment.emergencyLowBerths}
                        onChange={(e) => handleAssignmentChange(assignment.sectionId, 'emergencyLowBerths', Number(e.target.value))}
                        min={0}
                        step={1}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-gray-100"
                      />
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {assignment.emergencyLowBerths * EMERGENCY_LOW_BERTH_SPEC.tons} tons
                      </div>
                    </div>

                    {/* Common Area */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Common Area (tons)
                      </label>
                      <input
                        type="number"
                        value={assignment.commonArea}
                        onChange={(e) => handleAssignmentChange(assignment.sectionId, 'commonArea', Number(e.target.value))}
                        min={0}
                        step={0.5}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-gray-100"
                      />
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {formatCurrency(assignment.commonArea * COMMON_AREA_SPEC.costPerTon)}
                      </div>
                    </div>
                  </div>

                  {/* Section Totals */}
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-300">Section Cost:</span>
                        <span className="font-semibold ml-2 text-gray-900 dark:text-gray-100">{formatCurrency(sectionTotals.cost)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-300">Section Weight:</span>
                        <span className="font-semibold ml-2 text-gray-900 dark:text-gray-100">{sectionTotals.tons.toFixed(1)} tons</span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-300">Section Power:</span>
                        <span className="font-semibold ml-2 text-gray-900 dark:text-gray-100">{sectionTotals.power} Power</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Common Areas */}
        <div className="mb-6 bg-gray-50 dark:bg-gray-700 rounded-lg p-4 shadow">
          <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <span className="text-purple-500">👥</span> Common Areas
          </h3>
          <div className="mb-4 flex gap-6 items-start">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Common Area Size (tons)
              </label>
              <input
                type="number"
                value={stateroomConfig?.commonArea || 0}
                onChange={(e) => {
                  // This will be handled by section assignments
                }}
                min={0}
                step={1}
                className="w-32 p-2 border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-600"
                disabled
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Configured via section assignments above
              </div>
              {optionalCommonAreaTonnage > 0 && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Common Area (Tons) from Optional Systems:{" "}
                  {formatNumber(optionalCommonAreaTonnage)}
                </p>
              )}
            </div>

            {stateroomConfig && (
              <div className="flex-1 space-y-2 text-sm">
                <p className="text-gray-900 dark:text-gray-100">
                  Recommended Size:{" "}
                  <span
                    className={`font-medium ${showCommonAreaWarning ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}
                  >
                    {formatNumber(recommendedCommonArea || 0)} tons
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Emergency Low Berths */}
        <div className="mb-6 bg-gray-50 dark:bg-gray-700 rounded-lg p-4 shadow">
          <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <span className="text-red-500">⚠️</span> Emergency Low Berths
          </h3>
          <div className="mb-4 flex gap-6 items-start">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Number of Emergency Low Berths
              </label>
              <input
                type="number"
                value={stateroomConfig?.emergencyLowBerths?.count || 0}
                onChange={(e) => {
                  // This will be handled by section assignments
                }}
                min={0}
                step={1}
                className="w-32 p-2 border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-600"
                disabled
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Configured via section assignments above
              </div>
              {stateroomConfig && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Emergency Capacity:{" "}
                  {(stateroomConfig.emergencyLowBerths?.count || 0) *
                    EMERGENCY_LOW_BERTH_SPEC.capacity}{" "}
                  People
                </p>
              )}
            </div>

          </div>
        </div>

        {/* Validation Errors */}
        {Object.keys(errors).length > 0 && (
          <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
            <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">Validation Errors</h4>
            <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
              {Object.entries(errors).map(([field, error]) => (
                <li key={field}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={Object.keys(errors).length > 0}
            className={`px-6 py-2 rounded-md font-medium ${
              Object.keys(errors).length > 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
            }`}
          >
            Save Configuration
          </button>
        </div>
      </div>

      {/* Right Column: Sticky Summary */}
      <div className="w-full md:w-96 md:sticky md:top-4 h-fit">
        <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded shadow space-y-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <span className="text-green-500">📊</span> Accommodation Summary
          </h4>

          {/* High Passage Section */}
          <div className="bg-white dark:bg-gray-700 p-3 rounded border-l-4 border-blue-500">
            <h5 className="font-medium text-blue-700 dark:text-blue-400 mb-2">High Passage</h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Passengers:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {hierarchicalAllocation.totals.high + hierarchicalAllocation.shortfalls.high}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Luxury Staterooms:</span>
                <span className="font-medium text-purple-600 dark:text-purple-400">
                  {hierarchicalAllocation.allocations.high.luxury}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">High Staterooms:</span>
                <span className="font-medium text-blue-600 dark:text-blue-400">
                  {hierarchicalAllocation.allocations.high.high}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Single Staterooms:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.allocations.high.single}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Total Allocated:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.totals.high}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Still Needed:</span>
                <span
                  className={`font-medium ${hierarchicalAllocation.shortfalls.high > 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}
                >
                  {hierarchicalAllocation.shortfalls.high}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Stewards Required:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{stewardsRequired.high}</span>
              </div>
            </div>
          </div>

          {/* Middle Passage Section */}
          <div className="bg-white dark:bg-gray-700 p-3 rounded border-l-4 border-green-500">
            <h5 className="font-medium text-green-700 dark:text-green-400 mb-2">Middle Passage</h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Passengers:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {hierarchicalAllocation.totals.middle + hierarchicalAllocation.shortfalls.middle}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Luxury Staterooms:</span>
                <span className="font-medium text-purple-600 dark:text-purple-400">
                  {hierarchicalAllocation.allocations.middle.luxury}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">High Staterooms:</span>
                <span className="font-medium text-blue-600 dark:text-blue-400">
                  {hierarchicalAllocation.allocations.middle.high}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Single Staterooms:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.allocations.middle.single}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Total Allocated:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.totals.middle}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Still Needed:</span>
                <span
                  className={`font-medium ${hierarchicalAllocation.shortfalls.middle > 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}
                >
                  {hierarchicalAllocation.shortfalls.middle}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Stewards Required:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{stewardsRequired.middle}</span>
              </div>
            </div>
          </div>

          {/* Basic Passage Section */}
          <div className="bg-white dark:bg-gray-700 p-3 rounded border-l-4 border-orange-500">
            <h5 className="font-medium text-orange-700 dark:text-orange-400 mb-2">Basic Passage</h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Passengers:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {hierarchicalAllocation.totals.basic + hierarchicalAllocation.shortfalls.basic}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Luxury Staterooms:</span>
                <span className="font-medium text-purple-600 dark:text-purple-400">
                  {hierarchicalAllocation.allocations.basic.luxury}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">High Staterooms:</span>
                <span className="font-medium text-blue-600 dark:text-blue-400">
                  {hierarchicalAllocation.allocations.basic.high}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Single Staterooms:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.allocations.basic.single}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Double Staterooms:</span>
                <span className="font-medium text-orange-600 dark:text-orange-400">
                  {hierarchicalAllocation.allocations.basic.double} ({hierarchicalAllocation.accommodated.basic.double} people)
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Barracks:</span>
                <span className="font-medium text-yellow-600 dark:text-yellow-400">
                  {hierarchicalAllocation.allocations.basic.barracks}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Cargo Space Cots:</span>
                <span className="font-medium text-red-600 dark:text-red-400">
                  {hierarchicalAllocation.allocations.basic.cots}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Total Allocated:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.totals.basic}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Still Needed:</span>
                <span
                  className={`font-medium ${hierarchicalAllocation.shortfalls.basic > 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}
                >
                  {hierarchicalAllocation.shortfalls.basic}
                </span>
              </div>
            </div>
          </div>

          {/* Low Berth Section */}
          <div className="bg-white dark:bg-gray-700 p-3 rounded border-l-4 border-red-500">
            <h5 className="font-medium text-red-700 dark:text-red-400 mb-2">Low Berth</h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Passengers:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {hierarchicalAllocation.totals.low + hierarchicalAllocation.shortfalls.low}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Low Berths Allocated:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.allocations.low.lowBerths}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Still Needed:</span>
                <span
                  className={`font-medium ${hierarchicalAllocation.shortfalls.low > 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}
                >
                  {hierarchicalAllocation.shortfalls.low}
                </span>
              </div>
            </div>
          </div>

          {/* Crew Requirements Section */}
          <div className="bg-white dark:bg-gray-700 p-3 rounded border-l-4 border-indigo-500">
            <h5 className="font-medium text-indigo-700 dark:text-indigo-400 mb-2">
              Crew Requirements
            </h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Total Crew:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{Number(assignedCrewCount)}</span>
              </div>
                             <div className="flex justify-between">
                 <span className="text-gray-600 dark:text-gray-300">Crew Barracks Accommodation:</span>
                 <span
                   className={`font-medium ${barracksCrewCapacity > 0 ? "text-green-600 dark:text-green-400" : "text-gray-900 dark:text-gray-100"}`}
                 >
                   {barracksCrewCapacity}
                 </span>
               </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Crew Needing Staterooms:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {hierarchicalAllocation.totals.crew + hierarchicalAllocation.shortfalls.crew}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Luxury Staterooms:</span>
                <span className="font-medium text-purple-600 dark:text-purple-400">
                  {hierarchicalAllocation.allocations.crew.luxury}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">High Staterooms:</span>
                <span className="font-medium text-blue-600 dark:text-blue-400">
                  {hierarchicalAllocation.allocations.crew.high}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Single Staterooms:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.allocations.crew.single}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Double Staterooms:</span>
                <span className="font-medium text-orange-600 dark:text-orange-400">
                  {hierarchicalAllocation.allocations.crew.double} ({hierarchicalAllocation.accommodated.crew.double} people)
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Barracks:</span>
                <span className="font-medium text-yellow-600 dark:text-yellow-400">
                  {hierarchicalAllocation.allocations.crew.barracks}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Total Allocated:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {hierarchicalAllocation.totals.crew}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Still Needed:</span>
                <span
                  className={`font-medium ${hierarchicalAllocation.shortfalls.crew > 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}
                >
                  {hierarchicalAllocation.shortfalls.crew}
                </span>
              </div>
            </div>
          </div>

          {/* Passenger Cargo Reserve */}
          <div className="bg-white dark:bg-gray-700 p-3 rounded border-l-4 border-gray-500">
            <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-2">
              Passenger Cargo Reserve
            </h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Total Cargo Space Required:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {formatNumber(totalPassengerCargoTons)} tons
                </span>
              </div>
            </div>
          </div>

          {/* Ship Totals */}
          <div className="bg-white dark:bg-gray-700 p-3 rounded border-l-4 border-blue-500">
            <h5 className="font-medium text-blue-700 dark:text-blue-400 mb-2">Ship Totals</h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Total Cost:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(shipTotals.cost)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Total Weight:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{shipTotals.tons.toFixed(1)} tons</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Total Power:</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{shipTotals.power} Power</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BreakawayStateroomStep;
